/**
 * Details zur gewählten Zeile: am Desktop als Spalte neben der Liste, am Handy als Blatt mit allen
 * Fakten. Entscheidungen tragen zusätzlich Strategie und Herleitung des Preises.
 */
import type { DynamicPricingDecisionHistoryItem } from '@/shared/api';
import { BottomSheet } from '@/shared/components';
import { PriceDerivation } from '../PriceDerivation';
import { parseContributions } from '../priceDerivationModel';
import { HistoryDetailView } from './HistoryDetailView';
import type { HistoryRow } from './historyRows';
import { useHistoryStrategy } from './useHistoryStrategy';

const derivationSource = (item: DynamicPricingDecisionHistoryItem) => ({
  ...item,
  contributions: parseContributions(item.contributionsJson),
});

export function HistoryDetailPane({
  row,
  mobile,
  onClose,
}: {
  row: HistoryRow | undefined;
  mobile: boolean;
  onClose: () => void;
}) {
  const strategy = useHistoryStrategy(row?.decision?.strategyProfileVersionId);
  if (!row) return null;
  const detail = row.detail();
  if (row.decision) detail.facts.push(['Strategie', strategy]);
  const surface = mobile ? 'bg-paper' : 'bg-card';
  const body = (
    <HistoryDetailView detail={detail} surface={surface}>
      {row.decision && (
        <PriceDerivation
          className={surface}
          source={derivationSource(row.decision)}
          minimumStay={row.decision.minimumStayExplanation}
        />
      )}
    </HistoryDetailView>
  );
  if (mobile) {
    return (
      <BottomSheet
        open
        onOpenChange={(open) => !open && onClose()}
        title={detail.title}
        description={detail.eyebrow}
      >
        <div className="flex flex-col gap-3 py-1">{body}</div>
      </BottomSheet>
    );
  }
  return (
    <aside
      aria-label="Details"
      className="bg-paper border-hairline flex w-[380px] shrink-0 flex-col gap-3 border-l p-[18px]"
    >
      <span className="text-forest font-mono text-[10px] font-semibold uppercase tracking-[0.16em]">
        {detail.eyebrow}
      </span>
      <h2 className="font-serif text-[22px] font-bold leading-tight tracking-tight">
        {detail.title}
      </h2>
      {body}
    </aside>
  );
}
